import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";
import {
  getAllRemixerUsers,
  suspendRemixerUser,
  activateRemixerUser,
  deleteRemixerUser,
  updateFeaturedRemixers,
} from "../../services/thunks/remixerUserThunks";

import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";

import deleteIcon from "../../assets/icons/delete.svg";
import viewIcon from "../../assets/icons/view.svg";
import editIcon from "../../assets/icons/edit.svg";
import suspendIcon from "../../assets/icons/suspend.svg";
import searchIcon from "../../assets/icons/search.svg";
import activeIcon from "../../assets/icons/active.svg";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/Buttons/CustomButton";
import CustomCheckbox from "../../components/Form/CustomCheckbox";
import ConfirmSuspendModal from "../../components/Modals/ConfirmSuspendModal";
import SearchBar from "../../components/common/SearchBar";
import Input from "../../components/Form/Input";

const RemixerOverview = () => {
  const auth = useSelector((state) => state.auth);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [doSuspendRemixer] = useThunk(suspendRemixerUser);
  const [doActivateRemixer] = useThunk(activateRemixerUser);
  const [doDeleteRemixer] = useThunk(deleteRemixerUser);
  const [doUpdateFeatured] = useThunk(updateFeaturedRemixers);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedRemixer, setSelectedRemixer] = useState(null);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  
  // Featured remixers state with ranking
  const [featuredRanks, setFeaturedRanks] = useState({}); // { remixerId: rank }
  const [initialFeaturedRanks, setInitialFeaturedRanks] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditingRanks, setIsEditingRanks] = useState(false);
  const [duplicateRanks, setDuplicateRanks] = useState(new Set()); // Track duplicate rank values
  
  // Store input refs to maintain focus
  const inputRefs = useRef({});
  const focusedInputId = useRef(null);

  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);
  const navigate = useNavigate();

  // Separate effect to detect changes and duplicates - runs independently
  useEffect(() => {
    // Find duplicate ranks
    const rankValues = Object.values(featuredRanks);
    const duplicates = new Set();
    const seen = new Set();
    
    rankValues.forEach(r => {
      if (seen.has(r)) {
        duplicates.add(r);
      }
      seen.add(r);
    });
    
    setDuplicateRanks(duplicates);
    
    const hasChanges = JSON.stringify(featuredRanks) !== JSON.stringify(initialFeaturedRanks);
    setHasChanges(hasChanges);
  }, [featuredRanks, initialFeaturedRanks]);

  // Restore focus after any re-render
  useEffect(() => {
    if (focusedInputId.current && inputRefs.current[focusedInputId.current]) {
      const input = inputRefs.current[focusedInputId.current];
      if (document.activeElement !== input) {
        input.focus();
      }
    }
  });

  const handleSuspend = async (id) => {
    const result = await doSuspendRemixer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Remixer suspended successfully.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleActivate = async (id) => {
    const result = await doActivateRemixer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Remixer activated successfully.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleDelete = async (id) => {
    const result = await doDeleteRemixer({ token: auth.token, id });
    if (result.success) {
      showToast("success", "Remixer deleted successfully.");
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error.message);
    }
  };

  const handleRankChange = useCallback((remixerId, rank) => {
    const numRank = parseInt(rank);
    
    setFeaturedRanks((prev) => {
      const newRanks = { ...prev };
      
      if (rank === "") {
        // Clear the rank - remove from featured
        delete newRanks[remixerId];
      } else if (numRank > 0 && !isNaN(numRank)) {
        // Valid positive number - add/update rank
        newRanks[remixerId] = numRank;
      } else {
        // Invalid input - do nothing
        return prev;
      }
      
      return newRanks;
    });
  }, []);

  const handleInputFocus = useCallback((remixerId) => {
    focusedInputId.current = remixerId;
  }, []);

  const handleInputBlur = useCallback(() => {
    focusedInputId.current = null;
  }, []);

  const handleEditFeatured = () => {
    setIsEditingRanks(true);
  };

  const handleCancelEdit = () => {
    setFeaturedRanks({ ...initialFeaturedRanks });
    setIsEditingRanks(false);
    setHasChanges(false);
    setDuplicateRanks(new Set());
    focusedInputId.current = null;
  };

  const handleSaveFeatured = async () => {
    // Get all remixers with ranks
    const rankedRemixers = Object.entries(featuredRanks);
    
    // Allow saving even with no featured remixers
    if (rankedRemixers.length === 0) {
      const result = await doUpdateFeatured({
        token: auth.token,
        featuredRanks: {},
      });

      if (result.success) {
        showToast("success", "Featured remixers updated successfully.");
        setInitialFeaturedRanks({});
        setHasChanges(false);
        setIsEditingRanks(false);
        setDuplicateRanks(new Set());
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showToast("error", result.error?.message || "Failed to update featured remixers.");
      }
      return;
    }

    // Check for duplicate ranks
    const ranks = Object.values(featuredRanks);
    const hasDuplicates = ranks.length !== new Set(ranks).size;
    
    if (hasDuplicates) {
      showToast("error", "Each remixer must have a unique rank.");
      return;
    }

    const result = await doUpdateFeatured({
      token: auth.token,
      featuredRanks: featuredRanks,
    });

    if (result.success) {
      showToast("success", "Featured remixers ranked successfully.");
      setInitialFeaturedRanks({ ...featuredRanks });
      setHasChanges(false);
      setIsEditingRanks(false);
      setDuplicateRanks(new Set());
      setRefreshTrigger((prev) => prev + 1);
    } else {
      showToast("error", result.error?.message || "Failed to update featured remixers.");
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      setRefreshTrigger((prev) => prev + 1);
    }
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
  };

  const handleViewRemixer = (remixer) => {
    navigate(`/remixer-user/view/${remixer._id}`);
  };

  const handleEditRemixer = (remixer) => {
    navigate(`/remixer-user/edit/${remixer._id}`);
  };

  const handleAddNewRemixer = () => {
    navigate("/remixer-user/add");
  };

  const tableColumns = useMemo(
    () => [
      {
        title: (
          <div style={{ paddingLeft: "1rem" }} className="flex items-center">
            Rank
          </div>
        ),
        dataIndex: "featuredRank",
        key: "featuredRank",
        minWidth: "120px",
        render: (_, record) => {
          // Initialize featured ranks from backend data on first render
          if (record.featuredRank && !(record._id in featuredRanks) && !(record._id in initialFeaturedRanks)) {
            // Use queueMicrotask to avoid setState during render
            queueMicrotask(() => {
              setFeaturedRanks(prev => {
                if (record._id in prev) return prev;
                return { ...prev, [record._id]: record.featuredRank };
              });
              setInitialFeaturedRanks(prev => {
                if (record._id in prev) return prev;
                return { ...prev, [record._id]: record.featuredRank };
              });
            });
          }

          if (isEditingRanks) {
            const hasDuplicateRank = duplicateRanks.has(featuredRanks[record._id]);
            
            return (
              <div className="min-h-[32px] flex items-center justify-center">
                <input
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[record._id] = el;
                    }
                  }}
                  type="number"
                  min="1"
                  value={featuredRanks[record._id] || ""}
                  onChange={(e) => handleRankChange(record._id, e.target.value)}
                  onFocus={() => handleInputFocus(record._id)}
                  onBlur={handleInputBlur}
                  placeholder="_"
                  className={`w-16 px-2 py-1 text-center bg-[#2D2D2D] border rounded text-[#E2E2E2] text-sm focus:outline-none ${
                    hasDuplicateRank 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-[#808080] focus:border-white'
                  }`}
                />
              </div>
            );
          }
          
          return (
            <div className="min-h-[32px] flex items-center justify-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
              {featuredRanks[record._id] || record.featuredRank || "-"}
            </div>
          );
        },
      },
      {
        title: <div style={{ paddingLeft: "2.375rem" }}>User ID</div>,
        dataIndex: "userId",
        key: "userId",
        minWidth: "150px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] pl-[2.375rem]">
            {text}
          </div>
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        minWidth: "150px",
        render: (text) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
            {text || "N/A"}
          </div>
        ),
      },
      {
        title: "Download",
        dataIndex: "downloads",
        key: "downloads",
        align: "center",
        minWidth: "150px",
        render: (count) => (
          <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
            {count || 0}
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        align: "center",
        minWidth: "150px",
        render: (status) => (
          <div
            className={`min-h-[32px] flex items-center text-sm font-[400] font-public-sans ${
              status === "Active" ? "text-[#07C01D]" : "text-[#DC6700]"
            }`}
          >
            {status || "Active"}
          </div>
        ),
      },
      {
        title: "Action",
        key: "actions",
        minWidth: "320px",
        render: (_, record) => (
          <div className="flex gap-[8px] justify-start items-center">
            <button
              onClick={() => handleViewRemixer(record)}
              className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#07C01D] rounded-[15px] text-[12px] text-[#07C01D] w-[75px]"
            >
              <img src={viewIcon} alt="view" className="w-[10px] h-[11px]" />
              View
            </button>

            <button
              onClick={() => handleEditRemixer(record)}
              className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#B4C007] rounded-[15px] text-[12px] text-[#B4C007] w-[69px]"
            >
              <img src={editIcon} alt="edit" className="w-[10px] h-[11px]" />
              Edit
            </button>

            {record.status === "Active" ? (
              <button
                onClick={() => {
                  setSelectedRemixer(record);
                  setShowSuspendConfirm(true);
                }}
                className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#DC6700] rounded-[15px] text-[12px] text-[#DC6700] w-[99px]"
              >
                <img
                  src={suspendIcon}
                  alt="suspend"
                  className="w-[10px] h-[11px]"
                />
                Suspend
              </button>
            ) : (
              <button
                onClick={() => handleActivate(record._id)}
                className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#07C01D] rounded-[15px] text-[12px] text-[#07C01D] w-[99px]"
              >
                <img
                  src={activeIcon}
                  alt="activate"
                  className="w-[10px] h-[11px]"
                />
                Activate
              </button>
            )}

            <button
              onClick={() => {
                setSelectedRemixer(record);
                setShowDeleteConfirm(true);
              }}
              className="flex items-center justify-center gap-[5px] px-[12px] py-[2px] border border-[#C00707] rounded-[15px] text-[12px] text-[#C00707] w-[85px]"
            >
              <img
                src={deleteIcon}
                alt="delete"
                className="w-[10px] h-[12px]"
              />
              Delete
            </button>
          </div>
        ),
      },
    ],
    [featuredRanks, isEditingRanks, duplicateRanks, initialFeaturedRanks, handleRankChange, handleInputFocus, handleInputBlur]
  );

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setRefreshTrigger={setRefreshTrigger}
          placeholder="Search remixers"
          icon={searchIcon}
          containerClass="bg-[#2D2D2D] border border-[#808080] rounded-[5px] px-3 w-full h-[45px]"
          inputClass="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public Sans'] pr-6"
          buttonClass="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
          iconClass="w-5 h-5"
          closeIconClass="w-4 h-4"
          onKeyPress={handleKeyPress}
        />
        <div className="flex gap-2">
          {isEditingRanks ? (
            <CustomButton
              useGradientBorder
              rounded="rounded-lg"
              textSize="text-[18px] leading-[24px]"
              padding="py-[10.5px] px-[13.5px]"
              font="font-public-sans"
              border="border-lg"
              type="button"
              width="w-[200px]"
              onClick={handleSaveFeatured}
            >
              Save Featured
            </CustomButton>
          ) : (
            <CustomButton
              useGradientBorder
              rounded="rounded-lg"
              textSize="text-[18px] leading-[24px]"
              padding="py-[10.5px] px-[13.5px]"
              font="font-public-sans"
              border="border-lg"
              type="button"
              width="w-[200px]"
              onClick={handleEditFeatured}
            >
              Edit Featured
            </CustomButton>
          )}
          
          <CustomButton
            useGradientBorder
            rounded="rounded-lg"
            textSize="text-[18px] leading-[24px]"
            padding="py-[10.5px] px-[13.5px]"
            font="font-public-sans"
            border="border-lg"
            type="button"
            width="w-[178px]"
            onClick={handleAddNewRemixer}
          >
            Add New Remixer
          </CustomButton>
        </div>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={getAllRemixerUsers}
          refreshTrigger={refreshTrigger}
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => ({
            ...params,
            token: auth.token,
            search: searchQuery.trim() || undefined,
          })}
          setPageCallback={(fn) => (pageSetterRef.current = fn)}
          searchQuery={searchQuery}
        />
      </div>

      <ConfirmDeleteModal
        visible={showDeleteConfirm}
        onConfirm={async () => {
          const remixerId = selectedRemixer?._id;
          setShowDeleteConfirm(false);
          setSelectedRemixer(null);
          
          if (remixerId) {
            await handleDelete(remixerId);
          }
        }}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedRemixer(null);
        }}
        title="Confirm Delete"
        content={`Are you sure you want to delete this artist? `}
      />

      <ConfirmSuspendModal
        visible={showSuspendConfirm}
        onConfirm={async () => {
          const remixerId = selectedRemixer?._id;
          setShowSuspendConfirm(false);
          setSelectedRemixer(null);
          
          if (remixerId) {
            await handleSuspend(remixerId);
          }
        }}
        onCancel={() => {
          setShowSuspendConfirm(false);
          setSelectedRemixer(null);
        }}
      />
    </div>
  );
};

export default RemixerOverview;